"use client";

import React, { createContext, useContext, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useCreatePageStore } from "@/stores/createPageStore";
import {
  isRenderableChatMessage,
  hasRenderableChatTranscript,
} from "@/lib/agent/renderableChatMessage";
import { REFERENCE_IMAGE_ACCEPT } from "@/lib/create/referenceImageUpload";
import {
  evaluateAgentActionReadiness,
  hasConfirmedBackDesignDirection,
} from "@/lib/agent/actionReadinessGuards";
import { planDesignChanges } from "@/lib/designer/applyDesignChanges";
import { getFullCatalogOptions } from "@/services/createAgentService.presets";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import {
  projectDesignerRenderAction,
  designerPresetsEqual,
  resolveDesignerChoice,
  designerChoiceKey,
  type DesignerReviewPanel,
  type DesignerRenderProjection,
} from "@/lib/designer/actionProjection";
import {
  designerRenderBlockedReason,
  projectDesignerRenderActionState,
} from "@/lib/designer/renderControl";
import type { DesignerChoice } from "@/lib/designer/contracts";
import type {
  AgentActionPayload,
  AgentWidget,
  PresetOption,
} from "@/types/createAgent";
import { useAuth } from "@/context/providers/AuthProvider";
import { useChatActions } from "./ChatActionsProvider";
import { usePresetDispatch } from "./chat/usePresetDispatch";
import { useChatUpload } from "./chat/useChatUpload";
import { useDisplayPresets } from "./chat/useDisplayPresets";
import LoginRequiredDialog from "./chat/LoginRequiredDialog";
import type { DesktopRenderActionState } from "./desktopRenderAction";
import { useApplyDesignChanges } from "./useApplyDesignChanges";

export const RESTORED_DESIGN_MESSAGE_ID = "restored-design-decision";

export function restoredDesignerBackWidget(
  prompt?: string
): AgentWidget | null {
  const presets = getFullCatalogOptions("backPrompt", { prompt }).slice(0, 3);
  return presets.length
    ? {
        id: "restored-back-choices",
        version: 1,
        type: "catalog_picker",
        active: true,
        title: "Choose a back style",
        presets,
      }
    : null;
}

interface DesignerWorkspaceController {
  sharedWorkspace: boolean;
  interaction: ReturnType<typeof usePresetDispatch>;
  visiblePresets: PresetOption[];
  renderProjection: DesignerRenderProjection;
  renderActionState: DesktopRenderActionState;
  frontReviewPanels: Record<string, DesignerReviewPanel>;
  setFrontReviewPanel: (messageId: string, panel: DesignerReviewPanel) => void;
}

const WorkspaceContext = createContext<DesignerWorkspaceController | null>(
  null
);

export function useDesignerWorkspace(): DesignerWorkspaceController | null {
  return useContext(WorkspaceContext);
}

/** Persistent interactions belong above both responsive panels, not inside their renderers. */
export function DesignerWorkspaceProvider({
  children,
  blockedReason,
  sharedWorkspace = true,
}: {
  children: React.ReactNode;
  blockedReason?: string | null;
  /** Standalone chat consumers retain their existing in-chat action controls. */
  sharedWorkspace?: boolean;
}) {
  const actions = useChatActions();
  const applyChanges = useApplyDesignChanges();
  const auth = useAuth();
  const projectEpoch = useCreatePageStore(state => state.projectActionEpoch);
  const identityKey = `${auth.user?.id ?? auth.session?.user.id ?? "guest"}:${projectEpoch}`;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [panelState, setPanelState] = useState<{
    scope: string;
    panels: Record<string, DesignerReviewPanel>;
  }>({ scope: identityKey, panels: {} });
  const frontReviewPanels =
    panelState.scope === identityKey ? panelState.panels : {};
  const agent = useCreatePageStore(useShallow(state => state.agent));
  const formData = useCreatePageStore(state => state.form.data);
  const renderedImages = useCreatePageStore(state => state.generation.images);
  const frontUrl = renderedImages.front.url;
  const quoteSubmitting = useCreatePageStore(
    state => state.ticket.designSubmitted
  );
  const loading = useCreatePageStore(
    state =>
      state.generation.progress.loading || state.ui.modification.isModifying
  );
  const addMessage = useCreatePageStore(state => state.addMessage);
  const { visiblePresets } = useDisplayPresets();
  let startUpload: (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void = () => {};
  const interaction = usePresetDispatch({
    ...actions,
    selectionScope: identityKey,
    startPresetUpload: (messageId, preset, source) =>
      startUpload(messageId, preset, source),
    onRequireLogin: () => setShowLoginModal(true),
  });
  const { uploadInputRef, startPresetUpload, handleChatUploadImages } =
    useChatUpload({
      sendAgentMessage: actions.sendAgentMessage,
      addMessage,
      setSelectedPresetLabel: interaction.setSelectedPresetLabel,
      clearSelectedPresetLabel: interaction.clearSelectedPresetLabel,
    });
  startUpload = startPresetUpload;

  const latestMessage = [...agent.conversation.messages]
    .reverse()
    .find(
      message =>
        message.role === "assistant" && isRenderableChatMessage(message)
    );
  const restoredBack =
    !hasRenderableChatTranscript(agent.conversation.messages) &&
    Boolean(frontUrl) &&
    (formData.frontApproved === true || agent.phase === "back_design");
  const restoredWidget = restoredBack
    ? restoredDesignerBackWidget(formData.prompt)
    : null;
  const messageId = restoredBack
    ? RESTORED_DESIGN_MESSAGE_ID
    : (latestMessage?.id ?? "");
  const widgets = restoredWidget
    ? [restoredWidget]
    : (latestMessage?.widgets ?? []);
  const authoredSurface =
    latestMessage?.presets !== undefined ||
    latestMessage?.widgets !== undefined;
  const presets = restoredBack
    ? []
    : latestMessage
      ? authoredSurface
        ? (latestMessage.presets ?? [])
        : visiblePresets
      : [];
  let selection: DesignerChoice | null =
    interaction.selectedChoicesByMessageId?.[messageId] ?? null;
  if (!selection) {
    // Legacy persisted labels are accepted only when they identify one exact
    // option within one active widget; duplicate labels need a fresh choice.
    const restoredSelections = widgets
      .filter(widget => widget.active === true)
      .flatMap(widget => {
        const label = widget.selection?.data?.selectedLabel;
        const exact = widget.selection?.data?.selectedPreset;
        const candidates = getWidgetPresets(widget).filter(preset =>
          exact && typeof exact === "object"
            ? designerPresetsEqual(preset, exact as PresetOption)
            : preset.label === label
        );
        return candidates.length === 1
          ? [
              {
                preset: candidates[0],
                source: {
                  messageId,
                  widgetId: widget.id,
                  widgetVersion: widget.version,
                },
              },
            ]
          : [];
      });
    selection = restoredSelections.length === 1 ? restoredSelections[0] : null;
  }
  const currentBlockedReason = designerRenderBlockedReason({
    layoutBlockedReason: blockedReason,
    quoteSubmitting: Boolean(quoteSubmitting),
    authLoading: Boolean(interaction.authLoading),
    workInFlight: Boolean(
      loading ||
      agent.conversation.isTyping ||
      agent.conversation.presetsLoading ||
      agent.activeRequestController ||
      interaction.processingPresetId
    ),
  });
  const renderProjection = projectDesignerRenderAction({
    messageId,
    presets,
    widgets,
    phase: agent.phase,
    hasFrontImage: Boolean(frontUrl),
    selection,
    frontReviewPanel: frontReviewPanels[messageId],
    blockedReason: currentBlockedReason,
  });
  const identityRef = React.useRef(identityKey);
  React.useLayoutEffect(() => {
    identityRef.current = identityKey;
  }, [identityKey]);
  const projectionRef = React.useRef(renderProjection);
  React.useLayoutEffect(() => {
    projectionRef.current = renderProjection;
  });
  const action = renderProjection.action;
  const projectedApply = React.useMemo(() => {
    if (!frontUrl || !action) return null;
    const planned = planDesignChanges({
      generation: { images: renderedImages },
      agent: { phase: agent.phase, designBrief: agent.designBrief },
      // Selecting already commits visual values. A later Details edit wins;
      // the original preset remains only the immutable source of the click.
      form: { data: formData },
    });
    return planned.hasPendingChanges ? planned : null;
  }, [
    action,
    frontUrl,
    formData,
    agent.phase,
    agent.designBrief,
    renderedImages,
  ]);
  const renderDescriptor = projectDesignerRenderActionState({
    projection: renderProjection,
    plannedApply: projectedApply,
    hasBackImage: Boolean(renderedImages.back.url),
    savedRenderIsCurrent: Boolean(
      renderedImages.front.designSpec &&
      (!renderedImages.back.url || renderedImages.back.designSpec)
    ),
  });
  const renderActionState: DesktopRenderActionState = {
    ...renderDescriptor,
    capability:
      renderDescriptor.capability && action
        ? {
            ...renderDescriptor.capability,
            execute: async options => {
              const current = useCreatePageStore.getState();
              if (
                current.projectActionEpoch !== projectEpoch ||
                identityRef.current !== identityKey ||
                current.agent.sessionId !== agent.sessionId ||
                current.agent.conversation.isTyping ||
                current.agent.conversation.presetsLoading ||
                current.agent.activeRequestController ||
                current.ticket.designSubmitted ||
                current.generation.progress.loading ||
                current.ui.modification.isModifying
              )
                return;
              const latestAcceptedMessage = [
                ...current.agent.conversation.messages,
              ]
                .reverse()
                .find(
                  message =>
                    message.role === "assistant" &&
                    isRenderableChatMessage(message)
                );
              if (messageId === RESTORED_DESIGN_MESSAGE_ID) {
                if (
                  hasRenderableChatTranscript(
                    current.agent.conversation.messages
                  )
                )
                  return;
              } else {
                if (latestAcceptedMessage?.id !== messageId) return;
                const currentChoice = resolveDesignerChoice(
                  messageId,
                  action.preset,
                  latestAcceptedMessage.widgets ?? [],
                  action.source
                );
                if (
                  !currentChoice ||
                  designerChoiceKey(currentChoice) !== designerChoiceKey(action)
                )
                  return;
              }
              // A click retained across a session/turn change cannot spend against
              // the newly current workspace; require the same exact descriptor.
              if (
                projectionRef.current.action?.key !== action.key ||
                projectionRef.current.unavailableReason
              )
                return;
              if (
                renderDescriptor.capability?.actionId ===
                  "generate_dress_back" &&
                !current.generation.images.back.url &&
                !evaluateAgentActionReadiness({
                  actionId: "generate_dress_back",
                  phase: current.agent.phase,
                  formState: current.form.data,
                  hasFrontImage: Boolean(current.generation.images.front.url),
                  hasBackImage: false,
                  frontApproved: current.form.data.frontApproved === true,
                  hasBackDesignDirection: hasConfirmedBackDesignDirection(
                    planDesignChanges(current).draft
                  ),
                }).allowed
              )
                return;
              let applied = false;
              const applyCommitted = async () => {
                applied = await applyChanges.apply(options);
              };
              if (action.kind === "selection") {
                if (messageId === RESTORED_DESIGN_MESSAGE_ID)
                  await interaction.handleRestoredPresetGenerate(
                    messageId,
                    action.preset,
                    projectedApply ? applyCommitted : undefined
                  );
                else
                  await interaction.handleSelectedPresetGenerate(
                    messageId,
                    action.preset,
                    action.source,
                    projectedApply ? applyCommitted : undefined
                  );
              } else if (planDesignChanges(current).hasPendingChanges) {
                await interaction.handleCommittedPresetApply(
                  action.preset,
                  applyCommitted
                );
              } else {
                await interaction.handlePresetClick(
                  action.preset,
                  action.source
                );
              }
              return applied;
            },
          }
        : null,
  };
  return (
    <WorkspaceContext.Provider
      value={{
        sharedWorkspace,
        interaction,
        visiblePresets,
        renderProjection,
        renderActionState,
        frontReviewPanels,
        setFrontReviewPanel: (id, panel) =>
          setPanelState(current => ({
            scope: identityKey,
            panels: {
              ...(current.scope === identityKey ? current.panels : {}),
              [id]: panel,
            },
          })),
      }}
    >
      {children}
      <input
        ref={uploadInputRef}
        type="file"
        accept={REFERENCE_IMAGE_ACCEPT}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="chat-preset-upload-input"
        onChange={handleChatUploadImages}
      />
      <LoginRequiredDialog
        open={showLoginModal}
        onOpenChange={open => !open && setShowLoginModal(false)}
      />
    </WorkspaceContext.Provider>
  );
}
